import { Button } from "@community-project-org/ui";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useState } from "react";
import type { JSX } from "react";
import { useNavigate } from "react-router-dom";

import { apiRequest, ApiRequestError } from "../../common/api/apiClient.js";
import { formatWhen } from "../../common/components/formatWhen.js";
import { Money } from "../../common/components/Money.js";
import { Stepper } from "../../common/components/Stepper.js";
import type { components } from "../../generated/api.js";

type ServiceDTO = components["schemas"]["ServiceDTO"];
type RentOptionsDTO = components["schemas"]["RentOptionsDTO"];
type RentStartDTO = components["schemas"]["RentStartDTO"];
type RentQuoteDTO = components["schemas"]["RentQuoteDTO"];
type BookingDTO = components["schemas"]["BookingDTO"];

const UNIT_LABEL: Record<RentOptionsDTO["unit"], [string, string]> = {
  HOUR: ["Stunde", "Stunden"],
  DAY: ["Tag", "Tage"],
  NIGHT: ["Nacht", "Nächte"],
};

/** Heute als Ortsdatum JJJJ-MM-TT in der Zeitzone des Orts. */
function todayIn(timeZone: string): string {
  return new Intl.DateTimeFormat("en-CA", { timeZone, year: "numeric", month: "2-digit", day: "2-digit" }).format(
    new Date(),
  );
}

/**
 * Miete mit Zeitraum. Stunden: Tag, Dauer, dann eine freie Startzeit. Tage und Nächte:
 * Anreise- oder Abholdatum und Dauer; Preis und Verfügbarkeit rechnet der Server, bevor
 * gebucht wird. Ob es beim Absenden noch frei ist, sagt erst die Antwort.
 */
export function RentBooking({ offer }: { offer: ServiceDTO }): JSX.Element {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const timeZone = offer.venueTimeZone ?? "Europe/Berlin";
  const [date, setDate] = useState(() => todayIn(timeZone));
  const [units, setUnits] = useState<number | null>(null);
  const [start, setStart] = useState<string | null>(null);

  const options = useQuery({
    queryKey: ["offer-rent", offer.id],
    queryFn: () => apiRequest<RentOptionsDTO>(`/offers/${encodeURIComponent(offer.id)}/rent`, { tenant: null }),
  });
  const unit = options.data?.unit;
  const count = units ?? options.data?.minUnits ?? 1;
  const base = `/offers/${encodeURIComponent(offer.id)}/rent`;

  const starts = useQuery({
    queryKey: ["offer-rent-starts", offer.id, date, count],
    enabled: unit === "HOUR",
    queryFn: () => apiRequest<RentStartDTO[]>(`${base}/starts?date=${date}&units=${count}`, { tenant: null }),
  });
  const quoteStart = unit === "HOUR" ? start : date;
  const quote = useQuery({
    queryKey: ["offer-rent-quote", offer.id, quoteStart, count],
    enabled: Boolean(unit && quoteStart),
    retry: false,
    queryFn: () =>
      apiRequest<RentQuoteDTO>(`${base}/quote?start=${encodeURIComponent(quoteStart ?? "")}&units=${count}`, {
        tenant: null,
      }),
  });

  const book = useMutation({
    mutationFn: () =>
      apiRequest<BookingDTO>("/bookings", {
        method: "POST",
        body: { serviceId: offer.id, startsAt: quoteStart, units: count },
        tenant: null,
      }),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ["my-bookings"] });
      navigate("/tickets");
    },
    onError: async (error) => {
      if (error instanceof ApiRequestError && error.status === 409) {
        setStart(null);
        await Promise.all([starts.refetch(), quote.refetch()]);
      }
    },
  });

  if (options.isPending) {
    return <p className="text-[14px] text-ink-3">Wird geladen …</p>;
  }
  if (options.isError || !unit) {
    return <p className="text-[14px] text-danger">Dieses Mietangebot ist gerade nicht erreichbar.</p>;
  }

  const [one, many] = UNIT_LABEL[unit];
  const available = quote.data && quote.data.free > 0;

  return (
    <div className="grid gap-4">
      <div className="flex flex-wrap items-end justify-between gap-3">
        <label className="grid gap-1 text-[12px] font-bold text-ink-2">
          {unit === "NIGHT" ? "Anreise" : unit === "DAY" ? "Abholung" : "Tag"}
          <input
            type="date"
            className="min-h-[44px] rounded-md border border-line bg-surface px-2 text-[14px] text-ink"
            value={date}
            min={todayIn(timeZone)}
            onChange={(event) => {
              setDate(event.target.value);
              setStart(null);
            }}
          />
        </label>
        <div className="grid gap-1">
          <span className="text-[12px] font-bold text-ink-2">{count === 1 ? one : many}</span>
          <Stepper
            label={many}
            value={count}
            min={options.data.minUnits}
            max={options.data.maxUnits}
            onChange={(value) => {
              setUnits(value);
              setStart(null);
            }}
          />
        </div>
      </div>

      {unit === "HOUR" && (
        <div role="group" aria-label="Beginn" className="grid grid-cols-3 gap-2 sm:grid-cols-5">
          {starts.isPending && <p className="col-span-full text-[13px] text-ink-3">Startzeiten werden geladen …</p>}
          {starts.data?.length === 0 && (
            <p className="col-span-full text-[13px] text-ink-3">An diesem Tag ist für diese Dauer nichts möglich.</p>
          )}
          {starts.data?.map((entry) => (
            <Button
              key={entry.startsAt}
              variant={start === entry.startsAt ? "primary" : "secondary"}
              aria-pressed={start === entry.startsAt}
              disabled={entry.free === 0}
              aria-label={`${formatWhen(entry.startsAt, timeZone)}, ${entry.free} frei`}
              onClick={() => setStart(entry.startsAt)}
            >
              <span className="num">
                {new Intl.DateTimeFormat("de-DE", { timeZone, hour: "2-digit", minute: "2-digit" }).format(
                  new Date(entry.startsAt),
                )}
              </span>
            </Button>
          ))}
        </div>
      )}

      {quote.data && (
        <p className="num text-[13px] font-bold text-ink-2">
          {formatWhen(quote.data.startsAt, timeZone)} – {formatWhen(quote.data.endsAt, timeZone)}
          {" · "}
          {available ? `${quote.data.free} frei` : "belegt"}
        </p>
      )}
      {quote.error && (
        <p className="text-[13px] text-ink-2">
          {quote.error instanceof ApiRequestError ? quote.error.message : "Für diesen Zeitraum gibt es kein Angebot."}
        </p>
      )}
      {book.error && (
        <p className="text-[13px] text-danger" role="alert">
          {book.error instanceof ApiRequestError ? book.error.message : "Das hat nicht geklappt."}
        </p>
      )}

      <div className="flex items-center justify-between gap-3">
        <span className="text-[17px] font-extrabold text-ink">
          <Money cents={offer.priceCents * count} currency={offer.currency} />
        </span>
        <Button disabled={!available || book.isPending} aria-busy={book.isPending} onClick={() => book.mutate()}>
          {book.isPending ? "Wird gebucht …" : "Mieten"}
        </Button>
      </div>
    </div>
  );
}
